import { useSyncExternalStore } from "react";

/**
 * The dev menu's switches that outlive the menu: things like the FPS
 * readout, which the panel turns on and the HUD keeps showing after the
 * panel closes. Dev builds only, like everything in this folder. Each
 * switch is remembered in localStorage so a reload doesn't lose it — a
 * playtester who wants the frame counter up wants it up all session.
 */
export interface DevSettings {
  /** Show the frame-rate readout in the corner of the HUD. */
  showFps: boolean;
}

const DEFAULTS: DevSettings = { showFps: false };

const STORAGE_KEY = "woodworking-tycoon-dev-settings";

function read(): DevSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

let settings: DevSettings = read();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): DevSettings {
  return settings;
}

export function setDevSetting<K extends keyof DevSettings>(
  key: K,
  value: DevSettings[K],
): void {
  settings = { ...settings, [key]: value };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error("Failed to save dev settings:", error);
  }
  for (const listener of listeners) listener();
}

/** The current dev settings, re-rendering the caller when one changes. */
export function useDevSettings(): DevSettings {
  return useSyncExternalStore(subscribe, getSnapshot);
}
